/**
 * Setu Quiz → Make Webhook Integration
 *
 * Sends quiz completion data to Make.com webhook for syncing to Google Sheets + BIK AI
 * Features: idempotency, retry queue with exponential backoff, UTM tracking
 *
 * Webhook: https://hook.eu2.make.com/7il7idvlw2c4jehun1w8kck0fr47s2fq
 */

(function (window) {
    "use strict";

    // ============================================================================
    // CONFIG
    // ============================================================================

    var CONFIG = {
        QUIZ_VENDOR: "setu-quiz",
        QUIZ_VERSION: "v1",
        MAKE_WEBHOOK:
            "https://hook.eu2.make.com/7il7idvlw2c4jehun1w8kck0fr47s2fq",
        SHOP_DOMAIN: "setu-nutrition.myshopify.com",
        SHOP_CURRENCY: "USD",
        MAX_RETRIES: 3,
        RETRY_DELAYS: [2000, 8000, 32000], // Exponential backoff: 2s, 8s, 32s
        QUEUE_CHECK_INTERVAL: 10000, // Check queue every 10s
        STORAGE_KEY: "setuQuiz.webhookQueue",
    };

    // ============================================================================
    // UTILITY FUNCTIONS
    // ============================================================================

    /**
     * Strip HTML tags and clean up text for webhook
     */
    function cleanText(text) {
        if (!text) return "";
        
        // Create a temporary div to decode HTML entities
        var temp = document.createElement("div");
        temp.innerHTML = text;
        
        // Get text content (strips all HTML tags)
        var cleaned = temp.textContent || temp.innerText || "";
        
        // Remove emoji shortcodes like :red_circle:
        cleaned = cleaned.replace(/:[a-z_]+:/gi, "");
        
        // Trim extra whitespace
        cleaned = cleaned.replace(/\s+/g, " ").trim();
        
        return cleaned;
    }

    /**
     * Get cookie value by name
     */
    function getCookie(name) {
        var nameEQ = name + "=";
        var ca = document.cookie.split(";");
        for (var i = 0; i < ca.length; i++) {
            var c = ca[i];
            while (c.charAt(0) === " ") c = c.substring(1);
            if (c.indexOf(nameEQ) === 0)
                return c.substring(nameEQ.length, c.length);
        }
        return "";
    }

    /**
     * Generate UUID v4 with fallback for older browsers
     */
    function generateUUID() {
        if (typeof crypto !== "undefined" && crypto.randomUUID) {
            return crypto.randomUUID();
        }
        // Fallback: timestamp + random
        return (
            "xxxx-xxxx-xxxx-xxxx-xxxx".replace(/x/g, function () {
                return ((Math.random() * 16) | 0).toString(16);
            }) +
            "-" +
            Date.now().toString(36)
        );
    }

    /**
     * Get or create session ID (per-tab)
     */
    function getSessionId() {
        try {
            var sessionId = sessionStorage.getItem("setuQuiz.sessionId");
            if (!sessionId) {
                sessionId = generateUUID();
                sessionStorage.setItem("setuQuiz.sessionId", sessionId);
            }
            return sessionId;
        } catch (e) {
            // Fallback if sessionStorage unavailable
            return "session-" + Date.now().toString(36);
        }
    }

    /**
     * Extract all UTM parameters and click IDs from cookies
     */
    function getUTMParams() {
        return {
            source: getCookie("utm_source") || "",
            medium: getCookie("utm_medium") || "",
            campaign: getCookie("utm_campaign") || "",
            content: getCookie("utm_content") || "",
            term: getCookie("utm_term") || "",
            gclid: getCookie("gclid") || "",
            fbclid: getCookie("fbclid") || "",
        };
    }

    /**
     * Extract product handle and variant ID from results section
     */
    function extractProductInfo(currentPath) {
        var resultsSection = document.querySelector(
            '[data-section-type="results"][data-show-for-path="' +
                currentPath +
                '"]'
        );

        if (!resultsSection) {
            return [];
        }

        var productRecommendation = resultsSection.querySelector(
            ".product-recommendation"
        );
        if (!productRecommendation) {
            return [];
        }

        var handle =
            productRecommendation.getAttribute("data-product-handle") || "";
        var variantId =
            productRecommendation.getAttribute("data-variant-id") || "";

        if (!handle) {
            return [];
        }

        return [
            {
                handle: handle,
                variant_id: variantId || null,
            },
        ];
    }

    /**
     * Extract recommendation copy from results section
     */
    function extractRecommendationCopy(currentPath) {
        var resultsSection = document.querySelector(
            '[data-section-type="results"][data-show-for-path="' +
                currentPath +
                '"]'
        );

        if (!resultsSection) {
            return "";
        }

        var recommendationText = resultsSection.querySelector(
            ".recommendation-text"
        );
        if (recommendationText) {
            return recommendationText.textContent.trim();
        }

        var resultsTitle = resultsSection.querySelector(".results-title");
        if (resultsTitle) {
            return resultsTitle.textContent.trim();
        }

        return "";
    }

    /**
     * Get friendly label for result path
     */
    function getResultLabel(path) {
        var labels = {
            acne: "AcneClear Solution",
            radiant: "Radiant Skin Solution",
        };
        return labels[path] || path;
    }

    /**
     * Flatten formData into individual question fields for easy Google Sheets mapping
     * Returns ALL questions with empty strings for unanswered ones (stable structure for Make)
     */
    function buildFlattenedResponses(formData) {
        // Define ALL possible questions with empty defaults
        // This ensures Make always gets the same fields regardless of path or skipped answers
        var flattened = {
            // Q1 - Always present (routing)
            primary_concern: "",
            
            // Q2 - Path specific (only one will be filled)
            acne_pattern: "",
            aging_concern: "",
            
            // Q3 - Optional checkbox
            additional_goals: "",
            
            // Q4 - Always present
            current_approach: "",
            
            // Q5 - Optional checkbox
            past_disappointments: "",
            
            // Q6 - Path specific, optional checkbox (only one will be filled)
            acne_triggers: "",
            lifestyle_factors: "",
            
            // Q7-10 - Always present
            time_commitment: "",
            special_event: "",
            age_range: "",
            skin_sensitivity: ""
        };

        // Now fill in actual answers from formData
        for (var questionId in flattened) {
            if (!flattened.hasOwnProperty(questionId)) continue;
            
            // Check if this question was answered in formData
            if (!formData.hasOwnProperty(questionId)) {
                // Not answered - leave as "" (already set in defaults)
                continue;
            }

            var answer = formData[questionId];

            // Handle both single answers and multiple checkbox answers
            if (answer.values && Array.isArray(answer.values)) {
                // Multiple choice (checkboxes)
                if (answer.values.length > 0) {
                    var cleanLabels = answer.labels
                        ? answer.labels.map(function(label) { return cleanText(label); })
                        : [];
                    flattened[questionId] = cleanLabels.join(", ");
                }
                // If empty array, leave as "" (already set in defaults)
            } else if (answer.label && answer.value) {
                // Single choice (radio)
                flattened[questionId] = cleanText(answer.label);
            }
        }

        return flattened;
    }

    // ============================================================================
    // PAYLOAD BUILDER
    // ============================================================================

    /**
     * Build complete payload matching Make webhook schema
     */
    function buildPayload(formData, currentPath) {
        var timestamp = new Date().toISOString();
        var utm = getUTMParams();
        var flattenedResponses = buildFlattenedResponses(formData);
        var recommendedProducts = extractProductInfo(currentPath);
        var recommendationCopy = extractRecommendationCopy(currentPath);

        // Extract email from formData if captured
        var email = "";
        if (formData.email && formData.email.value) {
            email = formData.email.value.trim();
        }

        // Build base payload
        var payload = {
            vendor: CONFIG.QUIZ_VENDOR,
            quiz_version: CONFIG.QUIZ_VERSION,
            event: "quiz_completed",
            idempotency_key: generateUUID(),
            timestamp_iso: timestamp,
            session_id: getSessionId(),
            page_url: window.location.href,
            referrer: document.referrer || "",
            utm_source: utm.source,
            utm_medium: utm.medium,
            utm_campaign: utm.campaign,
            utm_content: utm.content,
            utm_term: utm.term,
            gclid: utm.gclid,
            fbclid: utm.fbclid,
            shop_domain: CONFIG.SHOP_DOMAIN,
            shop_currency: CONFIG.SHOP_CURRENCY,
            contact_email: email,
            contact_phone: "",
            contact_first_name: "",
            contact_last_name: "",
            contact_country: "",
            marketing_consent: email !== "" ? "true" : "false",
            result_id: currentPath,
            result_label: getResultLabel(currentPath),
            result_score: 0,
            recommended_product_handle: recommendedProducts.length > 0 ? recommendedProducts[0].handle : "",
            recommended_product_variant: recommendedProducts.length > 0 ? recommendedProducts[0].variant_id : "",
            recommendation_copy: recommendationCopy,
            user_agent: navigator.userAgent || "",
            language: navigator.language || "",
        };

        // Merge flattened responses into payload (q1_primary_concern, q2_acne_pattern, etc.)
        for (var key in flattenedResponses) {
            if (flattenedResponses.hasOwnProperty(key)) {
                payload[key] = flattenedResponses[key];
            }
        }

        return payload;
    }

    // ============================================================================
    // RETRY QUEUE MANAGEMENT
    // ============================================================================

    /**
     * Get retry queue from localStorage
     */
    function getQueue() {
        try {
            var queueStr = localStorage.getItem(CONFIG.STORAGE_KEY);
            return queueStr ? JSON.parse(queueStr) : [];
        } catch (e) {
            console.warn("[SetuQuizWebhook] Failed to read queue:", e);
            return [];
        }
    }

    /**
     * Save retry queue to localStorage
     */
    function saveQueue(queue) {
        try {
            localStorage.setItem(CONFIG.STORAGE_KEY, JSON.stringify(queue));
        } catch (e) {
            console.warn("[SetuQuizWebhook] Failed to save queue:", e);
        }
    }

    /**
     * Add payload to retry queue
     */
    function enqueue(payload) {
        var queue = getQueue();
        queue.push({
            payload: payload,
            attempts: 0,
            nextRetry: Date.now() + CONFIG.RETRY_DELAYS[0],
            addedAt: Date.now(),
        });
        saveQueue(queue);
    }

    /**
     * Remove successfully sent payload from queue
     */
    function dequeue(payload) {
        var queue = getQueue();
        var filtered = queue.filter(function (item) {
            return item.payload.idempotency_key !== payload.idempotency_key;
        });
        saveQueue(filtered);
    }

    /**
     * Process retry queue
     */
    function processQueue() {
        var queue = getQueue();
        var now = Date.now();
        var updatedQueue = [];

        queue.forEach(function (item) {
            // Skip if not ready to retry
            if (now < item.nextRetry) {
                updatedQueue.push(item);
                return;
            }

            // Discard if max retries exceeded
            if (item.attempts >= CONFIG.MAX_RETRIES) {
                console.warn(
                    "[SetuQuizWebhook] Max retries exceeded for:",
                    item.payload.idempotency_key
                );
                return; // Don't add back to queue
            }

            sendToWebhook(item.payload, function (success) {
                if (!success) {
                    // Failed, update retry info
                    item.attempts++;
                    item.nextRetry =
                        now +
                        (CONFIG.RETRY_DELAYS[item.attempts] ||
                            CONFIG.RETRY_DELAYS[
                                CONFIG.RETRY_DELAYS.length - 1
                            ]);

                    if (item.attempts < CONFIG.MAX_RETRIES) {
                        var currentQueue = getQueue();
                        currentQueue.push(item);
                        saveQueue(currentQueue);
                    } else {
                        console.warn(
                            "[SetuQuizWebhook] Max retries reached, discarding:",
                            item.payload.idempotency_key
                        );
                    }
                }
                // If success, it's already removed by dequeue() in sendToWebhook
            });
        });

        // Save queue with items that weren't ready to retry yet
        if (updatedQueue.length > 0) {
            saveQueue(updatedQueue);
        }
    }

    // ============================================================================
    // WEBHOOK SENDER
    // ============================================================================

    /**
     * Send payload to Make webhook
     * Note: Make should deduplicate by idempotency_key to handle refresh/back button
     */
    function sendToWebhook(payload, callback) {
        fetch(CONFIG.MAKE_WEBHOOK, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            mode: "cors",
            body: JSON.stringify(payload),
        })
            .then(function (response) {
                // Success codes: 200, 201, 202, 204
                if (response.ok || response.status === 204) {
                    dequeue(payload);

                    // Mark as synced in sessionStorage
                    try {
                        sessionStorage.setItem("setuQuiz.synced", "true");
                    } catch (e) {}

                    if (callback) callback(true);
                    return;
                }

                // Non-2xx response
                console.warn(
                    "[SetuQuizWebhook] Non-2xx response:",
                    response.status
                );
                if (callback) callback(false);
            })
            .catch(function (error) {
                console.warn("[SetuQuizWebhook] Network error:", error);
                if (callback) callback(false);
            });
    }

    // ============================================================================
    // PUBLIC API
    // ============================================================================

    var SetuQuizWebhook = {
        /**
         * Main entry point - send quiz completion to Make
         * @param {Object} formData - Quiz answers from SetuQuiz.formData
         * @param {String} currentPath - Result path ('acne' or 'radiant')
         */
        send: function (formData, currentPath) {
            if (!formData || !currentPath) {
                console.warn("[SetuQuizWebhook] Missing required data");
                return;
            }

            var payload = buildPayload(formData, currentPath);

            // Fire and forget - don't block UI
            sendToWebhook(payload, function (success) {
                if (!success) {
                    // Queue for retry
                    enqueue(payload);
                }
            });
        },

        /**
         * Manually process retry queue (exposed for debugging)
         */
        processQueue: processQueue,

        /**
         * Get current queue status (exposed for debugging)
         */
        getQueueStatus: function () {
            var queue = getQueue();
            return {
                size: queue.length,
                items: queue.map(function (item) {
                    return {
                        idempotency_key: item.payload.idempotency_key,
                        attempts: item.attempts,
                        nextRetry: new Date(item.nextRetry).toISOString(),
                    };
                }),
            };
        },
    };

    // ============================================================================
    // INITIALIZATION
    // ============================================================================

    // Process queue on page load
    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", function () {
            processQueue();
        });
    } else {
        processQueue();
    }

    // Process queue periodically
    setInterval(processQueue, CONFIG.QUEUE_CHECK_INTERVAL);

    // Process queue when coming back online
    window.addEventListener("online", function () {
        processQueue();
    });

    // Expose to global scope
    window.SetuQuizWebhook = SetuQuizWebhook;
})(window);
